/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import PropTypes from 'prop-types'
import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'

// Components
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {
    AspectRatio,
    Box,
    Skeleton as ChakraSkeleton,
    Stack,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Other
import Link from '@salesforce/retail-react-app/app/components/link'
import {productUrlBuilder} from '../../utils/url'
import {DEFAULT_LOCALE, styleConfigChakraKeys} from '../../constants'
import ProductPricing from '../product-pricing'

// const IconButtonWithRegistration = withRegistration(IconButton)

// Component Skeleton
export const Skeleton = () => {
    const styles = useMultiStyleConfig(styleConfigChakraKeys.productTile)
    return (
        <Box data-testid="sf-product-tile-skeleton">
            <Stack spacing={2}>
                <Box {...styles.imageWrapper}>
                    <AspectRatio ratio={1} {...styles.image}>
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
                <ChakraSkeleton width="80px" height="20px" />
                <ChakraSkeleton
                    width={{base: '120px', md: '220px'}}
                    height="12px"
                />
            </Stack>
        </Box>
    )
}

/** TOOLKIT CUSTOMIZATION **/
const ProductBadge = (props) => {
    const styles = useMultiStyleConfig(styleConfigChakraKeys.productBadge, {
        variant: props.attr
    })
    return (
        <Box __css={styles.container}>
            <Box as="p" __css={styles.text}>
                {props.text}
            </Box>
        </Box>
    )
}
ProductBadge.propTypes = {
    attr: PropTypes.string,
    text: PropTypes.string
}
/** END TOOLKIT CUSTOMIZATION **/

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
const ProductScrollerTile = (props) => {
    const intl = useIntl()
    const {product, dynamicImageProps, ...rest} = props

    const {image, productId, fallbackProductId} = product

    // ProductTile is used by two components, RecommendedProducts and ProductList.
    // RecommendedProducts provides a localized product name as `name` and non-localized product
    // name as `productName`. ProductList provides a localized name as `productName` and does not
    // use the `name` property.
    const localizedProductName = product.name
    const brandName = product?.brand?.name || product?.c_brand

    // const isFavouriteLoading = useRef(false)
    const styles = useMultiStyleConfig(
        styleConfigChakraKeys.productScrollerTile
    )

    const urlKey = useMemo(() => {
        const slug =
            intl.locale === DEFAULT_LOCALE
                ? product?.c_extend?.c_productsUniqueName_EN
                : product?.c_extend?.c_productsUniqueName_FR

        return slug && slug?.length ? slug : productId || fallbackProductId
    }, [product, productId])

    return (
        <Box {...styles.container}>
            <Link
                data-testid="product-tile"
                to={productUrlBuilder({id: urlKey}, intl.local)}
                {...styles.link}
                {...rest}
            >
                <Box {...styles.imageWrapper}>
                    {/** TOOLKIT CUSTOMIZATION **/}
                    {/* {product.badges?.length &&
                        product.badges.map((badge) => (
                            <ProductBadge
                                text={badge}
                                attr={badge}
                                key={badge}
                            />
                        ))} */}
                    {/** END TOOLKIT CUSTOMIZATION **/}
                    {image && (
                        <AspectRatio {...styles.image} ratio={1 / 1}>
                            <DynamicImage
                                src={image.desktop.url}
                                widths={dynamicImageProps?.widths}
                                imageProps={{
                                    alt: image.desktop.title,
                                    height: '100%',
                                    ...dynamicImageProps?.imageProps
                                }}
                            />
                        </AspectRatio>
                    )}
                </Box>
                {brandName && (
                    <Text
                        {...styles.brandName}
                        fontSize={['10px', '10px', '14px']}
                        fontWeight="300"
                    >
                        {brandName}
                    </Text>
                )}
                {/* Title */}
                {localizedProductName && (
                    <Text
                        {...styles.title}
                        fontSize={['14px', '14px', '20px']}
                        fontWeight={'300'}
                        noOfLines={1}
                        fontFamily="Raleway"
                        _hover={{textDecoration: 'underline'}}
                    >
                        {localizedProductName}
                    </Text>
                )}
                {/* Price */}
                {/** TOOLKIT CUSTOMIZATION **/}
                {product && <ProductPricing product={product} scope="tile" />}
                {/** END TOOLKIT CUSTOMIZATION **/}
            </Link>
        </Box>
    )
}

ProductScrollerTile.displayName = 'ProductScrollerTile'

ProductScrollerTile.propTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        name: PropTypes.string,
        c_extend: PropTypes.object,
        brand: PropTypes.object,
        c_brand: PropTypes.string,
        regularPrice: PropTypes.number,
        salePrice: PropTypes.number,
        currency: PropTypes.string,
        badges: PropTypes.arrayOf(PropTypes.string),
        image: PropTypes.shape({
            desktop: PropTypes.shape({
                url: PropTypes.string,
                title: PropTypes.string
            })
        }),
        productId: PropTypes.string,
        fallbackProductId: PropTypes.string
    }),
    /**
     * Enable adding/removing product as a favourite.
     * Use case: wishlist.
     */
    enableFavourite: PropTypes.bool,
    /**
     * Display the product as a favourite.
     */
    isFavourite: PropTypes.bool,
    /**
     * Callback function to be invoked when the user
     * interacts with favourite icon/button.
     */
    onFavouriteToggle: PropTypes.func,
    dynamicImageProps: PropTypes.object
}

export default ProductScrollerTile
